import { useCallback, useEffect, useState } from "react";
import UiIcon from "./UiIcon";
import { AdminBadge, AdminCallout, AdminDialog, AdminEmpty, AdminField, AdminPanel, AdminSwitch } from "./admin/AdminUi";
import {
    KPI_DEFAULT_PARAMETERS, KPI_METHODS, describeKpiParameters, isStandardKpiTemplate,
    kpiMethodCopy, kpiParameterChips, kpiTemplateName, validateKpiTemplateForm,
} from "./admin/kpiTemplateCopy";

const API = (process.env.REACT_APP_API_BASE_URL ?? "").replace(/\/+$/, "");
const copy = {
    en: {
        standardTitle: "Standard calculation methods", standardIntro: "Provided by CVI. Their definitions are locked; you can choose which ones are offered when an indicator is configured.",
        customTitle: "Custom templates", customIntro: "Organization-specific variants with their own parameters or formula. A template that is already assigned to an indicator cannot be redefined.",
        create: "New custom template", createTitle: "New KPI calculation template", createIntro: "Choose a method, adjust its parameters, and give the template a clear name.",
        name: "Template name", namePlaceholder: "e.g., Service level — 95% to 105%", method: "Calculation method", expression: "Formula", expressionHint: "Use Actual, Target, numbers and + − * / ( ). Example: (Actual / Target) * 100",
        parameters: "Parameters", tolerance: "Tolerance (% of target)", lower: "Lower bound (% of target)", upper: "Upper bound (% of target)", rangeTolerance: "Drop-off (percentage points to reach 0)",
        points: "Threshold points", pointX: "Achievement (% of target)", pointY: "Score", addPoint: "Add point", removePoint: "Remove point",
        save: "Create template", cancel: "Cancel", working: "Saving…", enabled: "Available for indicators", active: "Active", inactive: "Inactive", standard: "Standard", custom: "Custom",
        error: "Could not load KPI templates.", required: "Enter a name and a valid formula.", updated: "Template saved.",
        noCustom: "No custom templates yet", noCustomHint: "Standard methods cover most indicators. Create a custom template only when a specific band, threshold or formula is required.",
        formula: "Formula", howItWorks: "How it works", noScore: "A target of zero or less, or a negative actual value, produces no score. Results are bounded by the CVI calculation engine.",
        summaryActive: (a, t) => `${a} of ${t} available`, loading: "Loading templates…",
    },
    fr: {
        standardTitle: "Méthodes de calcul standard", standardIntro: "Fournies par CVI. Leur définition est verrouillée; vous choisissez celles qui sont offertes lors de la configuration d’un indicateur.",
        customTitle: "Modèles personnalisés", customIntro: "Variantes propres à l’organisation avec leurs propres paramètres ou leur formule. Un modèle déjà attribué à un indicateur ne peut pas être redéfini.",
        create: "Nouveau modèle personnalisé", createTitle: "Nouveau modèle de calcul KPI", createIntro: "Choisissez une méthode, ajustez ses paramètres et donnez un nom clair au modèle.",
        name: "Nom du modèle", namePlaceholder: "p. ex. Niveau de service — 95\u00a0% à 105\u00a0%", method: "Méthode de calcul", expression: "Formule", expressionHint: "Utilisez Actual, Target, des nombres et + − * / ( ). Exemple : (Actual / Target) * 100",
        parameters: "Paramètres", tolerance: "Tolérance (% de la cible)", lower: "Borne inférieure (% de la cible)", upper: "Borne supérieure (% de la cible)", rangeTolerance: "Décroissance (points de pourcentage jusqu’à 0)",
        points: "Seuils", pointX: "Atteinte (% de la cible)", pointY: "Résultat", addPoint: "Ajouter un seuil", removePoint: "Retirer le seuil",
        save: "Créer le modèle", cancel: "Annuler", working: "Enregistrement…", enabled: "Offert pour les indicateurs", active: "Actif", inactive: "Inactif", standard: "Standard", custom: "Personnalisé",
        error: "Impossible de charger les modèles.", required: "Saisissez un nom et une formule valide.", updated: "Modèle enregistré.",
        noCustom: "Aucun modèle personnalisé", noCustomHint: "Les méthodes standard couvrent la plupart des indicateurs. Créez un modèle personnalisé seulement si une plage, des seuils ou une formule particulière sont requis.",
        formula: "Formule", howItWorks: "Fonctionnement", noScore: "Une cible nulle ou négative, ou une valeur réelle négative, ne produit aucun résultat. Les résultats sont bornés par le moteur de calcul du CVI.",
        summaryActive: (a, t) => `${a} sur ${t} offerts`, loading: "Chargement des modèles…",
    },
};

const cloneDefaults = method => JSON.parse(JSON.stringify(KPI_DEFAULT_PARAMETERS[method] || {}));

function TemplateCard({ item, language, t, busy, onToggle }) {
    const method = kpiMethodCopy(item.method, language);
    const standard = isStandardKpiTemplate(item);
    const active = item.isActive !== false;
    const chips = kpiParameterChips(item, language);
    return <article className={`adm-template${active ? "" : " is-inactive"}`}>
        <div className="adm-template-head">
            <span className="adm-template-icon" aria-hidden="true">{method.symbol}</span>
            <div style={{ minWidth: 0 }}>
                <h3 className="adm-template-title">{kpiTemplateName(item, language)}</h3>
                <div className="adm-template-meta">
                    <AdminBadge tone={standard ? "navy" : "accent"} plain>{standard ? t.standard : t.custom}</AdminBadge>
                    {!standard && <AdminBadge tone="neutral" plain>{method.label}</AdminBadge>}
                    <AdminBadge tone={active ? "success" : "neutral"}>{active ? t.active : t.inactive}</AdminBadge>
                </div>
            </div>
        </div>
        <p className="adm-template-desc">{method.use}</p>
        <div>
            <div className="adm-section-label" style={{ marginBottom: 6 }}>{t.formula}</div>
            <code className="adm-formula">{item.method === "CUSTOM" ? (item.parameters?.expression || "—") : method.formula}</code>
            {chips.length > 0 && <div className="adm-params">{chips.map(([label, value]) => <span key={label} className="adm-param">{label} <b>{value}</b></span>)}</div>}
        </div>
        <p className="adm-template-desc">{describeKpiParameters(item, language)}</p>
        <div className="adm-template-foot">
            <AdminSwitch checked={active} disabled={busy} label={t.enabled}
                onChange={checked => onToggle(item, checked)} ariaLabel={`${t.enabled}: ${kpiTemplateName(item, language)}`} />
        </div>
    </article>;
}

function ParameterFields({ method, parameters, setParameters, t }) {
    const set = (key, value) => setParameters(current => ({ ...current, [key]: value === "" ? "" : Number(value) }));
    if (method === "TARGET") return <div className="adm-form-grid">
        <AdminField label={t.tolerance}><input type="number" min="1" step="any" value={parameters.tolerance ?? ""} onChange={e => set("tolerance", e.target.value)} /></AdminField>
    </div>;
    if (method === "RANGE") return <div className="adm-form-grid is-3">
        <AdminField label={t.lower}><input type="number" step="any" value={parameters.lower ?? ""} onChange={e => set("lower", e.target.value)} /></AdminField>
        <AdminField label={t.upper}><input type="number" step="any" value={parameters.upper ?? ""} onChange={e => set("upper", e.target.value)} /></AdminField>
        <AdminField label={t.rangeTolerance}><input type="number" min="1" step="any" value={parameters.tolerance ?? ""} onChange={e => set("tolerance", e.target.value)} /></AdminField>
    </div>;
    if (method === "THRESHOLD") {
        const points = parameters.points || [];
        const updatePoint = (index, key, value) => setParameters(current => ({
            ...current, points: (current.points || []).map((point, i) => i === index ? { ...point, [key]: value === "" ? "" : Number(value) } : point),
        }));
        return <div className="adm-stack-sm">
            <table className="adm-mini-table" style={{ fontSize: 12 }}>
                <thead><tr><th scope="col">{t.pointX}</th><th scope="col">{t.pointY}</th><th scope="col"><span className="adm-sr">{t.removePoint}</span></th></tr></thead>
                <tbody>{points.map((point, index) => <tr key={index}>
                    <td><input className="adm-control" aria-label={`${t.pointX} ${index + 1}`} type="number" step="any" value={point.x} onChange={e => updatePoint(index, "x", e.target.value)} /></td>
                    <td><input className="adm-control" aria-label={`${t.pointY} ${index + 1}`} type="number" min="0" max="200" step="any" value={point.y} onChange={e => updatePoint(index, "y", e.target.value)} /></td>
                    <td style={{ width: 40 }}><button type="button" className="adm-icon-btn is-danger" aria-label={t.removePoint} disabled={points.length <= 2}
                        onClick={() => setParameters(current => ({ ...current, points: current.points.filter((_, i) => i !== index) }))}><UiIcon name="close" size={14} /></button></td>
                </tr>)}</tbody>
            </table>
            <div><button type="button" className="adm-btn adm-btn--sm" disabled={points.length >= 12}
                onClick={() => setParameters(current => {
                    const list = current.points || [];
                    const last = list[list.length - 1] || { x: 100, y: 100 };
                    return { ...current, points: [...list, { x: Number(last.x || 0) + 10, y: Number(last.y || 0) }] };
                })}><UiIcon name="plus" size={13} />{t.addPoint}</button></div>
        </div>;
    }
    return null;
}

export default function KpiFormulaTemplates({ language = "en" }) {
    const t = copy[language === "fr" ? "fr" : "en"];
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [name, setName] = useState("");
    const [method, setMethod] = useState("CUSTOM");
    const [parameters, setParameters] = useState(() => cloneDefaults("CUSTOM"));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [formError, setFormError] = useState("");
    const [message, setMessage] = useState("");
    const [creating, setCreating] = useState(false);
    const request = useCallback(async (path, options = {}) => {
        const token = sessionStorage.getItem("cvi_token");
        const response = await fetch(API + "/api/indicators/calculation-templates" + path, {
            ...options, headers: { "Content-Type": "application/json", Authorization: "Bearer " + token, ...(options.headers || {}) },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || t.error);
        return payload;
    }, [t.error]);
    const load = useCallback(async () => {
        try { setItems(await request("")); setError(""); }
        catch (e) { setError(e.message); }
        finally { setLoaded(true); }
    }, [request]);
    useEffect(() => { load(); }, [load]);
    const save = async (path, body, methodName) => {
        setBusy(true); setError(""); setMessage("");
        try { await request(path, { method: methodName, body: JSON.stringify(body) }); await load(); setMessage(t.updated); return true; }
        catch (e) { setError(e.message); return false; }
        finally { setBusy(false); }
    };
    const chooseMethod = next => { setMethod(next); setParameters(cloneDefaults(next)); setFormError(""); };
    const openCreate = () => { setName(""); chooseMethod("CUSTOM"); setFormError(""); setCreating(true); };
    const create = async event => {
        event?.preventDefault();
        const invalid = validateKpiTemplateForm({ name, method, parameters }, language);
        if (invalid) { setFormError(invalid); return; }
        setBusy(true); setFormError(""); setMessage("");
        try {
            await request("", { method: "POST", body: JSON.stringify({ name: name.trim(), method, parameters }) });
            await load(); setMessage(t.updated); setCreating(false); setName("");
        } catch (e) { setFormError(e.message); }
        finally { setBusy(false); }
    };
    const toggle = (item, checked) => save("/" + encodeURIComponent(item.id), { isActive: checked }, "PATCH");

    const standardItems = items.filter(isStandardKpiTemplate);
    const customItems = items.filter(item => !isStandardKpiTemplate(item));
    const activeCount = list => list.filter(item => item.isActive !== false).length;
    const preview = { method, parameters };

    return <div className="adm-stack" data-cvi-i18n="managed">
        {error && <AdminCallout tone="danger">{error}</AdminCallout>}
        {message && <div role="status"><AdminCallout tone="success">{message}</AdminCallout></div>}

        <AdminPanel title={<>{t.standardTitle} <span className="adm-count">{t.summaryActive(activeCount(standardItems), standardItems.length)}</span></>}
            description={t.standardIntro}>
            {!loaded ? <AdminEmpty compact icon="target" title={t.loading} /> :
                <div className="adm-template-grid">
                    {standardItems.map(item => <TemplateCard key={item.id} item={item} language={language} t={t} busy={busy} onToggle={toggle} />)}
                </div>}
        </AdminPanel>

        <AdminPanel title={<>{t.customTitle} <span className="adm-count">{customItems.length}</span></>} description={t.customIntro}
            actions={<button type="button" className="adm-btn adm-btn--primary" onClick={openCreate}><UiIcon name="plus" size={14} />{t.create}</button>}>
            {customItems.length === 0
                ? <AdminEmpty compact icon="target" title={t.noCustom} description={t.noCustomHint} />
                : <div className="adm-template-grid">
                    {customItems.map(item => <TemplateCard key={item.id} item={item} language={language} t={t} busy={busy} onToggle={toggle} />)}
                </div>}
        </AdminPanel>

        <AdminCallout tone="neutral">{t.noScore}</AdminCallout>

        {creating && <AdminDialog size="lg" title={t.createTitle} description={t.createIntro} onClose={() => setCreating(false)} language={language}
            dirty={Boolean(name.trim()) || method !== "CUSTOM" || JSON.stringify(parameters) !== JSON.stringify(KPI_DEFAULT_PARAMETERS.CUSTOM)}
            closeLabel={t.cancel}
            footer={({ requestClose }) => <>
                <button type="button" className="adm-btn" onClick={requestClose}>{t.cancel}</button>
                <button type="submit" form="adm-kpi-create" className="adm-btn adm-btn--primary" disabled={busy}>{busy ? t.working : t.save}</button>
            </>}>
            <form id="adm-kpi-create" onSubmit={create} data-cvi-i18n="managed">
                {formError && <div className="adm-form-error" role="alert"><UiIcon name="warning" size={15} /><span>{formError}</span></div>}
                <div className="adm-form-section">
                    <AdminField label={t.name} required><input value={name} onChange={e => setName(e.target.value)} maxLength={120} placeholder={t.namePlaceholder} /></AdminField>
                </div>
                <div className="adm-form-section">
                    <h3 className="adm-form-section-title">{t.method}</h3>
                    <div className="adm-choice-grid" role="radiogroup" aria-label={t.method}>
                        {KPI_METHODS.map(id => {
                            const info = kpiMethodCopy(id, language);
                            return <label key={id} className={`adm-choice${method === id ? " is-selected" : ""}`}>
                                <input type="radio" name="adm-kpi-method" value={id} checked={method === id} onChange={() => chooseMethod(id)} />
                                <span className="adm-choice-copy"><strong><span aria-hidden="true">{info.symbol}</span>{info.label}</strong><small>{info.use}</small></span>
                            </label>;
                        })}
                    </div>
                </div>
                <div className="adm-form-section">
                    {method !== "CUSTOM" && <h3 className="adm-form-section-title">{t.parameters}</h3>}
                    {method === "CUSTOM"
                        ? <AdminField label={t.expression} hint={t.expressionHint} required>
                            <input dir="ltr" value={parameters.expression || ""} maxLength={200} onChange={e => setParameters(current => ({ ...current, expression: e.target.value }))}
                                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }} />
                        </AdminField>
                        : <ParameterFields method={method} parameters={parameters} setParameters={setParameters} t={t} />}
                    {method !== "CUSTOM" && <div style={{ marginTop: 12 }}><code className="adm-formula">{kpiMethodCopy(method, language).formula}</code></div>}
                    <p className="adm-hint" style={{ marginTop: 10 }}><strong>{t.howItWorks}: </strong>{describeKpiParameters(preview, language)}</p>
                </div>
            </form>
        </AdminDialog>}
    </div>;
}
